import { 
  Box, 
  Text, 
  VStack, 
  useColorModeValue,
  SkeletonText,
  Center,
  Button,
  IconButton,
  Tooltip,
  useToast
} from '@chakra-ui/react';
import { useRef, useEffect, useState, useCallback } from 'react';
import { useProgress } from '../context/ProgressContext';
import { useLyricsTheme } from '../context/LyricsThemeContext';
import { spotifyService } from '../services/spotifyService';

// Update the WrenchIcon component to be larger
const WrenchIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
  </svg>
);

export interface LyricsProps {
  lyrics: {
    lyrics: string;
    synced: boolean;
    syncedLyrics?: Record<string, string>;
    source?: string;
  } | null;
  currentLyricLine: string;
  currentLyricTimestamp?: number;
  isLoading: boolean;
  progressMs: number;
  textColor?: string;
  mutedTextColor?: string;
  onManualLineSelect?: (lineText: string, timestamp: number) => void;
  currentTrackId?: string;
  onResumeAutoTracking?: () => void;
  isActive: boolean;
  onManualSelect: (lyricText: string, timestamp: number) => void;
  onSettingsOpen?: () => void; // Add new prop for opening settings
  frostedEffect?: boolean; // Use frosted effect instead of glass
  frostedOpacity?: number; // Opacity for frosted effect
  spotifyToken?: string | null; // Spotify access token for API calls
  isSpotifyActive?: boolean; // Whether Spotify is the active music service
}

const Lyrics = ({ 
  lyrics, 
  currentLyricLine, 
  currentLyricTimestamp,
  isLoading, 
  progressMs, 
  textColor: customTextColor, 
  mutedTextColor: customMutedTextColor,
  onManualLineSelect,
  currentTrackId,
  onResumeAutoTracking,
  isActive,
  onManualSelect,
  onSettingsOpen,
  frostedEffect: propFrostedEffect = false, // Default value if not provided
  frostedOpacity: propFrostedOpacity = 0.3, // Default value if not provided
  spotifyToken = null,
  isSpotifyActive = false
}: LyricsProps) => {
  // Add toast for notifications
  const toast = useToast();
  
  // Debug log to check if onSettingsOpen is defined
  // console.log("Lyrics component: onSettingsOpen is:", onSettingsOpen ? "defined" : "undefined");
  
  // Get progress context
  const { 
    manualSelectedLyric,
    userScrolling, 
    setUserScrolling,
    clearManualStates,
    trackIdJustChanged
  } = useProgress();
  
  // Get lyrics theme context
  const { 
    currentTheme, 
    isTemplateDark, 
    dominantColor,
    colorMode,
    isDarkMode,
    isLightMode,
    fontSize,
    lineSpacing,
    activeTextColor,
    inactiveTextColor,
    currentLineScale = 1.1,
    currentLineFontWeight = 0.6,
    transitionType = 'bounce',
    backgroundType,
    backgroundColor,
    backgroundGradient,
    frostedEffect: contextFrostedEffect,
    frostedOpacity: contextFrostedOpacity,
    letterSpacing
  } = useLyricsTheme();
  
  // Use context values with fallback to props
  const frostedEffect = contextFrostedEffect !== undefined ? contextFrostedEffect : (propFrostedEffect || false);
  const frostedOpacity = contextFrostedOpacity !== undefined ? contextFrostedOpacity : (propFrostedOpacity || 0.3);
  
  // Add console log to debug colors
  // console.log('Lyrics colors:', { activeTextColor, inactiveTextColor });
  
  const currentLineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const lastScrollTime = useRef<number>(0);
  const [manualMode, setManualMode] = useState(false);
  const [selectedLine, setSelectedLine] = useState<string>('');
  const prevTrackIdRef = useRef<string | undefined>(currentTrackId);
  const scrollTimeoutRef = useRef<number | null>(null);
  const isScrollingRef = useRef<boolean>(false); // Reference to track scrolling state
  const autoScrollTimeoutRef = useRef<number | null>(null); // Reference for auto-scroll timeout
  const [isSeekingToPosition, setIsSeekingToPosition] = useState(false);
  const [isScrollStalled, setIsScrollStalled] = useState(false);
  const [lastProcessedLyric, setLastProcessedLyric] = useState<string>('');
  // Track previous scroll count to prevent excessive scrolling
  const scrollCountRef = useRef<number>(0);
  
  // Function to forcibly reset all scrolling states (for debugging/recovery)
  const forceResetAllScrollStates = useCallback(() => {
    console.log('Forcibly resetting ALL scroll states');
    
    // Reset all state variables
    setIsScrolling(false);
    setManualMode(false);
    setUserScrolling(false);
    setSelectedLine('');
    setIsScrollStalled(false);
    
    // Reset all refs
    isScrollingRef.current = false;
    lastScrollTime.current = 0;
    
    // Clean up all timeouts
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    
    if (autoScrollTimeoutRef.current) {
      window.clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }
    
    // Clear context state
    clearManualStates();
    
    // Show success toast
    toast({
      title: "Scroll states reset",
      description: "Auto-scrolling has been reset",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
    // Force immediate scroll to current line
    setTimeout(() => {
      if (currentLineRef.current && containerRef.current) {
        const scrollPosition = currentLineRef.current.offsetTop - 20;
        containerRef.current.scrollTo({
          top: scrollPosition,
          behavior: 'auto'  // Use 'auto' for immediate scroll
        });
      }
    }, 50);
  }, [toast, clearManualStates, setUserScrolling]);
  
  // Check for stalled scrolling (hasn't been reset in a long time)
  useEffect(() => {
    // Set up interval to check for stalled scrolling
    const stalledScrollInterval = setInterval(() => {
      const now = Date.now();
      // If we've been in scrolling state for more than 3 seconds continuously
      if (isScrollingRef.current && (now - lastScrollTime.current > 3000)) {
        // console.log('Scrolling appears to be stalled!');
        setIsScrollStalled(true);
      } else {
        setIsScrollStalled(false);
      }
    }, 1000);
    
    return () => {
      clearInterval(stalledScrollInterval);
    };
  }, []);
  
  // Cleanup function for all timeouts
  const cleanupTimeouts = useCallback(() => {
    // Clear scroll timeout
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    
    // Clear auto-scroll timeout
    if (autoScrollTimeoutRef.current) {
      window.clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }
  }, []);
  
  // Use provided colors or default to the Chakra theme
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  
  // Reset manual mode when track changes
  useEffect(() => {
    if (currentTrackId !== prevTrackIdRef.current) {
      console.log('Track changed in Lyrics component, resetting ALL scroll and manual states');
      
      // Reset all local state
      setManualMode(false);
      setSelectedLine('');
      setIsScrolling(false);
      setLastProcessedLyric(''); // Reset the last processed lyric when track changes
      
      // Reset all refs
      isScrollingRef.current = false;
      lastScrollTime.current = 0;
      
      // Clean up all timeouts
      cleanupTimeouts();
      
      // Reset context state
      setUserScrolling(false);
      clearManualStates(); // Clear context state as well
      
      // Update track ID ref
      prevTrackIdRef.current = currentTrackId;
      
      // Reapply critical CSS variables that might get lost during track changes
      const criticalCssVars = {
        '--current-line-font-weight': currentLineFontWeight.toString(),
        '--lyrics-line-font-weight': (400 + (300 * currentLineFontWeight)).toString(),
      };
      
      // Apply to document root to ensure global availability
      Object.entries(criticalCssVars).forEach(([variable, value]) => {
        document.documentElement.style.setProperty(variable, value);
      });
      
      console.log('Reapplied CSS variables after track change:', criticalCssVars);
    }
  }, [currentTrackId, setUserScrolling, clearManualStates, cleanupTimeouts, currentLineFontWeight]);
  
  // Reset manual mode when progress is reset to 0 (song restart)
  useEffect(() => {
    // Check if progress is at or very close to the beginning (allowing for small timing differences)
    if (progressMs <= 50 && manualMode) {
      setManualMode(false);
      setSelectedLine('');
      clearManualStates(); // Clear context state as well
      // console.log('Progress reset to beginning, resetting manual mode');
    }
  }, [progressMs, manualMode, clearManualStates]);
  
  // Update the scrollToCurrentLine function to ensure styles are preserved
  const scrollToCurrentLine = useCallback(() => {
    // Skip if no line to scroll to, or required refs aren't available
    if (!currentLineRef.current || !containerRef.current || !lyrics?.syncedLyrics) {
      // console.log('Skipping scroll: missing refs or lyrics');
      return;
    }
    
    // Skip if in manual mode - manual mode should prevent auto-scrolling
    if (manualMode) {
      // console.log('Skipping scroll: manual mode active');
      return;
    }
    
    // Skip if user is actively scrolling
    if (userScrolling) {
      // console.log('Skipping scroll: user is actively scrolling');
      return;
    }
    
    // Check if this is the same lyric line we already processed
    // If so, only proceed if enough time has passed
    const now = Date.now();
    if (currentLyricLine === lastProcessedLyric) {
      // Only apply throttling for the same lyric line
      // This allows immediate scrolling for different lyric lines
      if (now - lastScrollTime.current < 200) {
        return;
      }

      // Additional check to prevent excessive scrolling for the same line
      // If we've already scrolled to this line recently and nothing has changed, don't scroll again
      if (now - lastScrollTime.current < 2000) {
        // console.log(`Skipping redundant scroll to same line: '${currentLyricLine}' (scrolled recently)`);
        return;
      }
    } else {
      // This is a different lyric line, update the tracking
      // console.log(`New lyric line detected: '${currentLyricLine}' (previous: '${lastProcessedLyric}')`);
      setLastProcessedLyric(currentLyricLine);
      
      // For a new line, we reset the isScrolling state to ensure it scrolls
      if (isScrollingRef.current) {
        // console.log('Forcing scroll for new lyric line');
        isScrollingRef.current = false;
        setIsScrolling(false);
      }
    }
    
    // Force reset isScrolling state if it's been true for too long (recovery mechanism)
    if (isScrollingRef.current) {
      const timeInScrollingState = now - lastScrollTime.current;
      if (timeInScrollingState > 1500) {
        // console.log('Forcing reset of isScrolling after timeout', timeInScrollingState);
        isScrollingRef.current = false;
        setIsScrolling(false);
      } else if (currentLyricLine === lastProcessedLyric) {
        // Only apply cooldown for the same lyric line
        // Still in cooldown period for the same line
        // console.log('Still in scrolling cooldown for same line, waiting');
        return;
      }
    }
    
    // Update last scroll time
    lastScrollTime.current = now;
    
    // Set a flag to indicate we're doing programmatic scrolling
    // Only set if we're not already in programmatic scrolling mode
    if (!isScrollingRef.current) {
      setIsScrolling(true);
      isScrollingRef.current = true;
      // console.log(`Starting programmatic scroll to current line: '${currentLyricLine}'`);
    }
    
    // Use a slightly longer delay to ensure DOM updates have settled
    setTimeout(() => {
      if (currentLineRef.current && containerRef.current) {
        // Check if we've already scrolled too many times in a short period
        scrollCountRef.current++;
        
        const lineElement = currentLineRef.current;
        
        // Force a layout recalculation to ensure accurate dimensions after font size changes
        // Reading offsetHeight here forces the browser to recalculate layout
        const elementHeight = lineElement.offsetHeight;
        
        // Make sure the current line has the style attributes set
        // This ensures custom font size and line spacing are applied
        if (lineElement) {
          // The style attributes are already set in the Box component,
          // but we'll ensure they're recognized when scrolling happens
          const currentLineStyles = window.getComputedStyle(lineElement);
          if (currentLineStyles.getPropertyValue('--font-size-factor') === '') {
            // Re-apply styles if they're missing (unlikely, but as a safeguard)
            (lineElement as HTMLElement).style.setProperty('--font-size-factor', fontSize.toString());
            (lineElement as HTMLElement).style.setProperty('--line-spacing-factor', lineSpacing.toString());
          }
        }
        
        // Calculate position to place the current line at the top with a dynamic padding
        // Use a percentage of the container height instead of fixed pixels
        // This adapts better to different font sizes
        const containerHeight = containerRef.current.clientHeight;
        const topOffset = Math.max(containerHeight * 0.05, 10); // Min 10px, or 5% of container height
        
        // Get the accurate current position after layout recalculation
        const scrollPosition = lineElement.offsetTop - topOffset;
        
        // Check if we're already close to the desired position
        // If so, don't scroll again to prevent jittery behavior
        const currentScrollPosition = containerRef.current.scrollTop;
        const scrollDifference = Math.abs(currentScrollPosition - scrollPosition);
        
        // Only scroll if we need to move more than a minimal threshold (15px)
        if (scrollDifference <= 15 && currentLyricLine === lastProcessedLyric) {
          console.log(`Skipping scroll - already close to target position (diff: ${scrollDifference}px)`);
          // Reset scrolling state since we're not actually scrolling
          setIsScrolling(false);
          isScrollingRef.current = false;
          return;
        }
        
        // Log the action for debugging
        // console.log(`Scrolling to position: ${scrollPosition}px for line: '${currentLyricLine}' (font size: ${fontSize}, line height: ${lineSpacing})`);
        
        // Apply the scroll with smooth behavior for a better user experience
        containerRef.current.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
        
        // Reset scrolling status after animation completes with enough buffer
        // for smooth scrolling to finish
        if (autoScrollTimeoutRef.current) {
          window.clearTimeout(autoScrollTimeoutRef.current);
        }
        
        autoScrollTimeoutRef.current = window.setTimeout(() => {
          setIsScrolling(false);
          isScrollingRef.current = false;
          autoScrollTimeoutRef.current = null;
          
          // Double-check if we're at the right position and adjust if needed
          // This helps when the initial scroll didn't fully account for layout changes
          if (currentLineRef.current && containerRef.current) {
            const finalScrollPosition = currentLineRef.current.offsetTop - topOffset;
            const currentScrollTop = containerRef.current.scrollTop;
            
            // If we're more than 20px off from where we should be, adjust
            if (Math.abs(currentScrollTop - finalScrollPosition) > 20) {
              console.log('Adjusting scroll position after animation - off by', 
                          Math.abs(currentScrollTop - finalScrollPosition));
              
              containerRef.current.scrollTo({
                top: finalScrollPosition,
                behavior: 'auto' // Use 'auto' for immediate correction
              });
            }
          }
          
          console.log('Auto-scroll completed, reset scroll states');
        }, 700); // Increased timeout to ensure scroll animation completes
      }
    }, 50); // Increased delay to allow layout updates to settle
  }, [currentLyricLine, lyrics?.syncedLyrics, userScrolling, manualMode, fontSize, lineSpacing, lastProcessedLyric]);
  
  // Auto-scroll to keep the current lyric centered
  useEffect(() => {
    // Check if we should initiate auto-scrolling to current lyric
    if (!manualMode && !userScrolling && currentLyricLine && currentLyricTimestamp) {
      // Use a shorter timeout to ensure we don't miss lyric changes
      const scrollTimer = setTimeout(() => {
        scrollToCurrentLine();
      }, 10);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [currentLyricLine, currentLyricTimestamp, scrollToCurrentLine, userScrolling, manualMode]);
  
  // Handle manual line selection
  const handleLineClick = async (lineText: string, timestamp: number) => {
    // If Spotify is active and we have a token, seek to the position in Spotify
    if (isSpotifyActive && spotifyToken) {
      setIsSeekingToPosition(true);
      
      try {
        const seekResult = await spotifyService.seekToPosition(spotifyToken, timestamp);
        
        if (seekResult) {
          toast({
            title: "Position updated",
            description: `Moved to ${new Date(timestamp).toISOString().substr(14, 8)}`,
            status: "success",
            duration: 2000,
            isClosable: true,
          });
          
          // If seeking was successful, explicitly resume source tracking
          // This will disable manual mode if it was enabled
          resumeTracking();
          setIsSeekingToPosition(false);
          return;
        } else {
          toast({
            title: "Couldn't update position",
            description: "Using manual mode instead",
            status: "warning",
            duration: 2000,
            isClosable: true,
          });
          // If seeking failed, fall back to manual mode
        }
      } catch (error) {
        console.error("Error seeking to position:", error);
        toast({
          title: "Error seeking to position",
          description: "Using manual mode instead",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
        // If seeking threw an error, fall back to manual mode
      }
      
      setIsSeekingToPosition(false);
    }
    
    // Standard manual mode behavior (fallback if seek fails or if not using Spotify)
    setManualMode(true);
    
    // Update selected line for visual feedback
    setSelectedLine(lineText);
    
    if (onManualLineSelect) {
      onManualLineSelect(lineText, timestamp);
    }
  };

  // Update the resumeTracking function to preserve style settings
  const resumeTracking = useCallback(() => {
    setManualMode(false);
    setSelectedLine('');
    setIsScrolling(false);
    isScrollingRef.current = false;
    
    // Clean up timeouts but preserve style settings
    cleanupTimeouts();
    
    // Clear tracking state but don't affect visual settings
    clearManualStates(); // Use the context clearManualStates function
    
    // Force an update of the current line with our font size settings
    // This ensures the styles are properly applied when auto-tracking resumes
    setTimeout(() => {
      // Trigger a re-render by scrolling to the current line
      if (currentLineRef.current && containerRef.current) {
        const lineElement = currentLineRef.current;
        // Use same position calculation as in scrollToCurrentLine
        const scrollPosition = lineElement.offsetTop - 20;
        
        containerRef.current.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 50);
    
    // Notify parent component to resume auto-tracking
    if (onResumeAutoTracking) {
      onResumeAutoTracking();
    }
  }, [cleanupTimeouts, clearManualStates, onResumeAutoTracking]);
  
  // Handler for user scroll interactions
  const handleUserScroll = useCallback(() => {
    // Only react to user scrolling, not programmatic scrolling
    // This is the key check to prevent false activations
    if (isScrollingRef.current) {
      // console.log('Ignoring scroll event - programmatic scrolling in progress');
      return;
    }
    
    // Also check if track just changed - ignore scrolling during track transitions
    if (trackIdJustChanged) {
      // console.log('Ignoring scroll event - track just changed');
      return;
    }
    
    // Also check if we're within the debounce window from the last programmatic scroll
    const now = Date.now();
    const timeSinceLastAutoScroll = now - lastScrollTime.current;
    const DEBOUNCE_THRESHOLD = 1000; // 1 second debounce threshold
    
    if (timeSinceLastAutoScroll < DEBOUNCE_THRESHOLD) {
      // console.log(`Ignoring scroll event - too soon after auto-scroll (${timeSinceLastAutoScroll}ms)`);
      return;
    }
    
    // Set user scrolling state to true
    setUserScrolling(true);
    console.log('User scroll detected, pausing auto-tracking');
    
    // Clean up any existing timeouts
    cleanupTimeouts();
    
    // Set a timeout to re-enable auto-tracking after 2 seconds of inactivity
    scrollTimeoutRef.current = window.setTimeout(() => {
      console.log('User finished scrolling, resuming auto-tracking');
      
      // When in manual mode we need to fully reset it
      if (manualMode) {
        console.log('Auto-resuming tracking after 2 seconds of no scrolling in manual mode');
        
        // Flash the current line to indicate auto-resumption of tracking
        if (currentLineRef.current) {
          const currentLine = currentLineRef.current;
          
          // Add a flash animation class
          currentLine.classList.add('auto-resume-flash');
          
          // Remove the animation class after it completes
          setTimeout(() => {
            currentLine.classList.remove('auto-resume-flash');
          }, 1500); // Animation duration is 1.5s
        }
        
        resumeTracking(); // This will call clearManualStates and reset everything
      } else {
        // Just reset user scrolling for normal mode
        setUserScrolling(false);
      }
      
      scrollTimeoutRef.current = null;
    }, 2000);
  }, [setUserScrolling, cleanupTimeouts, trackIdJustChanged, manualMode, resumeTracking]);
  
  // Add and remove scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleUserScroll);
      
      return () => {
        container.removeEventListener('scroll', handleUserScroll);
        cleanupTimeouts();
      };
    }
  }, [handleUserScroll, cleanupTimeouts]);
  
  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      cleanupTimeouts();
    };
  }, [cleanupTimeouts]);
  
  // Find the index of the current line in the lyrics
  const getCurrentLineIndex = (lines: { time: number, text: string }[], currentLine: string): number => {
    return lines.findIndex(line => line.text === currentLine);
  };
  
  // Determine which line is current based on tracking
  const getCurrentLineInfo = (line: { time: number, text: string }, lineIndex: number, currentIndex: number) => {
    // Standard tracking - highlight based on current timestamp
    const isCurrentLine = line.text === currentLyricLine && 
                         currentLyricTimestamp !== undefined && 
                         Math.abs(line.time - currentLyricTimestamp) < 500;
                         
    // For Spotify theme, we also need to know if this is a past or future line
    // Make sure a line cannot be both current and future/past at the same time
    const isPastLine = isTemplateDark && lineIndex < currentIndex && !isCurrentLine;
    const isFutureLine = isTemplateDark && lineIndex > currentIndex && !isCurrentLine;
    
    return { isCurrentLine, isPastLine, isFutureLine };
  };
  
  // Create CSS variables for different transition types
  const getTransitionStyle = () => {
    switch (transitionType) {
      case 'bounce':
        return 'font-size 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), color 0.1s ease, opacity 0.3s ease';
      case 'smooth':
        return 'font-size 0.4s ease-in-out, transform 0.4s ease-in-out, color 0.2s ease, opacity 0.3s ease';
      case 'sharp':
        return 'font-size 0.2s ease, transform 0.2s ease, color 0.1s linear, opacity 0.2s linear';
      case 'none':
        return 'none';
      default:
        return 'font-size 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), color 0.1s ease, opacity 0.3s ease';
    }
  };

  // Define the transition style
  const currentLineTransition = getTransitionStyle();
  
  // Add an additional effect to handle font size changes specifically
  useEffect(() => {
    // This effect will run whenever fontSize or lineSpacing changes
    // It will force a scroll to the current line after a short delay
    // to ensure the UI has updated with the new font settings
    
    // Set a flag to track if font size just changed, to avoid excessive scrolls
    const fontSizeChanged = true;
    
    // Only try to scroll if we actually have a current line and container
    if (currentLineRef.current && containerRef.current) {
      // Wait a bit for the layout to update with new font sizes
      const fontChangeTimeout = setTimeout(() => {
        // Verify refs are still valid inside the timeout
        if (!currentLineRef.current || !containerRef.current) {
          console.log('Refs no longer valid during font change timeout');
          return;
        }
        
        // Only scroll if the user is not manually scrolling
        if (!userScrolling && !manualMode && !isScrollingRef.current) {
          console.log('Font size or spacing changed, forcing scroll refresh');
          
          // Calculate the position where the current line should be
          const containerHeight = containerRef.current.clientHeight;
          const topOffset = Math.max(containerHeight * 0.05, 10);
          const scrollPosition = currentLineRef.current.offsetTop - topOffset;
          
          // Check if we're already close to the desired position
          const currentScrollPosition = containerRef.current.scrollTop;
          const scrollDifference = Math.abs(currentScrollPosition - scrollPosition);
          
          // Only scroll if we need to move more than a minimal threshold (15px)
          if (scrollDifference > 15) {
            console.log(`Repositioning after font change to: ${scrollPosition} (diff: ${scrollDifference}px)`);
            containerRef.current.scrollTo({
              top: scrollPosition,
              behavior: 'auto' // Use 'auto' for immediate positioning
            });
          } else {
            console.log(`Skipping font change scroll - already close to target (diff: ${scrollDifference}px)`);
          }
        } else {
          console.log('Skipping font change scroll - user is scrolling or manual mode is active');
        }
      }, 100);
      
      return () => clearTimeout(fontChangeTimeout);
    }
  }, [fontSize, lineSpacing, userScrolling, manualMode]);
  
  // Show loading state
  if (isLoading) {
    return (
      <Box p={4}>
        <SkeletonText mt='4' noOfLines={10} spacing='4' skeletonHeight='2' />
      </Box>
    );
  }
  
  // Show a message if no lyrics are found
  if (!lyrics || !lyrics.syncedLyrics) {
    return (
      <Center h="100%" flexDirection="column" gap={4}>
        <Text fontSize="xl" color={textColor}>
          No lyrics available
        </Text>
      </Center>
    );
  }
  
  // Process synchronized lyrics
  const lyricLines = Object.entries(lyrics.syncedLyrics)
    .sort(([timeA], [timeB]) => parseInt(timeA) - parseInt(timeB))
    .map(([time, line]) => ({ time: parseInt(time), text: line }));
  
  // Find the current line index
  const currentIndex = getCurrentLineIndex(lyricLines, currentLyricLine);
  
  if (lyrics.synced) {  
    return (
      <Center height="100%" width="100%" position="relative">
        {isSpotifyActive && spotifyToken && (
          <Box
            position="absolute"
            top={2}
            left={2}
            zIndex={5}
            bg="rgba(0, 0, 0, 0.6)"
            color="#1DB954"
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
            display="flex"
            alignItems="center"
          >
            <Box
              width="6px"
              height="6px"
              borderRadius="50%"
              bg="#1DB954"
              mr={1}
              animation="pulse-green 2s infinite"
              sx={{
                "@keyframes pulse-green": {
                  "0%": { opacity: 0.4, transform: "scale(0.9)" },
                  "50%": { opacity: 1, transform: "scale(1.1)" },
                  "100%": { opacity: 0.4, transform: "scale(0.9)" }
                }
              }}
            />
            Click lyrics to seek
          </Box>
        )}

        {manualMode && (
          <Button
            position="absolute"
            top={2}
            right={2}
            colorScheme="blue"
            size="sm"
            onClick={resumeTracking}
            zIndex={5}
            className="manual-adjustment-button"
            bg="rgba(0, 0, 0, 0.6)"
            _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
          >
            RESUME SOURCE TRACKING
          </Button>
        )}

        {userScrolling && !manualMode && (
          <Button
            position="absolute"
            top={2}
            right={2}
            colorScheme="orange"
            size="sm"
            onClick={resumeTracking}
            zIndex={5}
            className="resume-tracking-button"
            bg="rgba(0, 0, 0, 0.6)"
            _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
          >
            RESUME SOURCE TRACKING
          </Button>
        )}

        {isScrollStalled && (
          <Button
            position="absolute"
            top={2}
            right={2}
            colorScheme="red"
            size="sm"
            onClick={forceResetAllScrollStates}
            zIndex={6}
            className="fix-scrolling-button"
            bg="rgba(220, 20, 60, 0.8)"
            _hover={{ bg: "rgba(220, 20, 60, 1)" }}
          >
            FIX SCROLLING
          </Button>
        )}

        {isSeekingToPosition && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.7)"
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Text mb={2} color="white" fontWeight="bold">Syncing with Spotify...</Text>
            <Box
              width="50px"
              height="50px"
              borderRadius="50%"
              border="3px solid"
              borderColor="#1DB954"
              borderTopColor="transparent"
              animation="spin 1s linear infinite"
              sx={{
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" }
                }
              }}
            />
          </Box>
        )}
        
        <Box 
          height="90%" 
          width={{ base: "95%", md: "90%", lg: "95%" }} 
          display="flex" 
          className={`lyrics-panel`}
          alignItems="center" 
          justifyContent="flex-start"
          ml={{ base: 0, md: "-5%", lg: "-10%" }}
        >
          <Box
            ref={containerRef}
            width="100%"
            height="100%"
            overflow="auto"
            position="relative"
            className={`lyrics-container ${!frostedEffect ? 'frost-disabled' : ''}`}
            sx={{
              perspective: '1000px',
              backfaceVisibility: 'hidden',
              willChange: 'transform',
              transformStyle: 'preserve-3d',
              // Set CSS variables for universal styling
              '--lyrics-line-font-size': `calc(28px * ${fontSize})`,
              '--lyrics-line-line-height': `calc(1.5 * ${lineSpacing})`,
              '--lyrics-line-margin-bottom': `calc(34px * ${lineSpacing})`,
              '--lyrics-line-letter-spacing': letterSpacing,
              '--current-line-scale': currentLineScale.toString(),
              '--current-line-transition': currentLineTransition,
              '--frosted-effect-opacity': frostedOpacity.toString(),
              '--current-line-font-weight': currentLineFontWeight.toString()
            }}
          >
            {/* Empty space at the top to improve scrolling */}
            <Box height="100px" />
            
            <Box className={`lyrics-focus-zone ${isLightMode ? 'light-mode' : 'dark-mode'}`}
                 position="relative" 
                 zIndex={2}>
              {lyricLines.map((line, index) => {
                const { isCurrentLine, isPastLine, isFutureLine } = getCurrentLineInfo(line, index, currentIndex);
                
                return (
                  <Box 
                    key={index}
                    width="100%"
                    textAlign="left"
                    ref={isCurrentLine ? currentLineRef : undefined}
                    className={`lyric-line ${isCurrentLine ? 'current-lyric-line' : ''} ${isPastLine ? 'past-lyric-line' : ''} ${isFutureLine ? 'future-lyric-line' : ''} ${manualMode ? 'clickable-lyric' : ''} ${isLightMode ? 'light-mode' : 'dark-mode'} ${isCurrentLine && !frostedEffect ? 'no-frost' : ''}`}
                    style={{
                      '--font-size-factor': fontSize,
                      '--line-spacing-factor': lineSpacing,
                      color: isCurrentLine ? activeTextColor : inactiveTextColor,
                      ...(isCurrentLine && frostedEffect ? {
                        backdropFilter: 'blur(10px)',
                        backgroundColor: isLightMode
                          ? `rgba(250, 250, 255, ${frostedOpacity})`
                          : `rgba(25, 25, 35, ${frostedOpacity})`,
                        borderRadius: '8px',
                        padding: '10px 20px',
                        margin: '-10px -20px'
                      } : {})
                    } as React.CSSProperties}
                    height={isCurrentLine ? 'auto' : 'auto'}
                    onClick={() => handleLineClick(line.text, line.time)}
                    cursor={(isSpotifyActive && spotifyToken) || manualMode ? "pointer" : "default"}
                    data-spotify-active={!!(isSpotifyActive && spotifyToken)}
                  >
                    {line.text || '♪'}
                  </Box>
                );
              })}
            </Box>
            
            {/* Empty space at the bottom to improve scrolling */}
            <Box height="100px" />
          </Box>
        </Box>
        
        {/* Wrench button for lyrics settings - UPDATED LARGER VERSION */}
        <Tooltip label="Lyrics Settings" placement="top">
          <IconButton
            aria-label="Lyrics Settings"
            icon={<WrenchIcon />}
            position="absolute"
            bottom={10}
            right={3}
            borderRadius="full"
            size="lg"
            onClick={() => {
              // Debug the onSettingsOpen function
              console.log("CLICK EVENT: Wrench button clicked in Lyrics component");
              console.log("onSettingsOpen type:", typeof onSettingsOpen);
              console.log("onSettingsOpen value:", onSettingsOpen);
              
              // Call onSettingsOpen if it's defined
              if (typeof onSettingsOpen === 'function') {
                console.log("Calling onSettingsOpen function from Lyrics component...");
                try {
                  // Direct call without any arguments
                  const result = onSettingsOpen();
                  console.log("onSettingsOpen call completed, result:", result);
                } catch (error) {
                  console.error("Error calling onSettingsOpen:", error);
                }
              } else {
                console.error("onSettingsOpen is not a function:", onSettingsOpen);
              }
            }}
            zIndex={5}
            className="lyrics-settings-button"
            bg="rgb(255, 255, 255)"
            _hover={{ bg: "rgba(65, 105, 225, 0.9)", transform: "scale(1.1)" }}
            boxShadow="0 4px 8px rgba(0, 0, 0, 0.3)"
            w="60px"
            h="60px"
            transition="all 0.2s ease-in-out"
            sx={{
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(65, 105, 225, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(65, 105, 225, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(65, 105, 225, 0)" }
              }
            }}
          />
        </Tooltip>
        
        {manualMode && (
          <Text
            position="absolute"
            bottom={4}
            left="50%"
            transform="translateX(-50%)"
            fontSize="sm"
            color="gray.300"
            bg="rgba(0, 0, 0, 0.5)"
            p={2}
            borderRadius="md"
          >
            Manual mode - tracking and auto-scrolling from selected point
          </Text>
        )}

        {userScrolling && !manualMode && (
          <Text
            position="absolute"
            bottom={30}
            left="50%"
            transform="translateX(-50%)"
            fontSize="sm"
            color="orange.200"
            bg="rgba(0, 0, 0, 0.5)"
            p={2}
            borderRadius="md"
          >
            Scroll mode active - auto-tracking paused
          </Text>
        )}
      </Center>
    );
  } else {
    // Non-synced lyrics display
    return (
      <Box overflowY="auto" h="100%" p={4} position="relative">
        {/* Frosted effect not applied in non-synced mode as we don't have a current line */}
        <Text whiteSpace="pre-wrap" fontFamily="monospace" position="relative" zIndex={2}>
          {lyrics.lyrics}
        </Text>
        
        {/* Wrench button for non-synced lyrics - UPDATED LARGER VERSION */}
        <Tooltip label="Lyrics Settings" placement="top">
          <IconButton
            aria-label="Lyrics Settings"
            icon={<WrenchIcon />}
            position="absolute"
            bottom={10}
            right={3}
            borderRadius="full"
            colorScheme="blue"
            size="lg"
            onClick={() => {
              // Debug the onSettingsOpen function
              console.log("CLICK EVENT: Wrench button clicked in Lyrics component");
              console.log("onSettingsOpen type:", typeof onSettingsOpen);
              console.log("onSettingsOpen value:", onSettingsOpen);
              
              // Call onSettingsOpen if it's defined
              if (typeof onSettingsOpen === 'function') {
                console.log("Calling onSettingsOpen function from Lyrics component...");
                try {
                  // Direct call without any arguments
                  const result = onSettingsOpen();
                  console.log("onSettingsOpen call completed, result:", result);
                } catch (error) {
                  console.error("Error calling onSettingsOpen:", error);
                }
              } else {
                console.error("onSettingsOpen is not a function:", onSettingsOpen);
              }
            }}
            zIndex={5}
            className="lyrics-settings-button"
            bg="rgb(255, 255, 255)"
            _hover={{ bg: "rgba(65, 105, 225, 0.9)", transform: "scale(1.1)" }}
            boxShadow="0 4px 8px rgba(0, 0, 0, 0.3)"
            w="60px"
            h="60px"
            transition="all 0.2s ease-in-out"
            sx={{
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(65, 105, 225, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(65, 105, 225, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(65, 105, 225, 0)" }
              }
            }}
          />
        </Tooltip>
      </Box>
    );
  }
};

export default Lyrics;